import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatSelect } from '@angular/material/select';
import { LocalMetaVendedorService, MetaVendedor, BulkLoadResponse, ResultadoCarga, ExcelRow } from '../../services/local-meta-vendedor.service';
import { VendedorService, Vendedor } from '../../services/vendedor.service';
import { NotificationHelper } from 'src/app/core/services/helpers/notification.helper';
import { hideSpinner, showSpinner } from 'src/app/core/services/functions.service';
import * as XLSX from 'xlsx';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { DetalleMetaDialogComponent } from '../detalle-meta-dialog/detalle-meta-dialog.component';
import { Observable, Subject, forkJoin } from 'rxjs';
import { takeUntil, debounceTime, map, startWith, finalize, tap } from 'rxjs/operators';
import { PeriodosmetasService, PeriodoMetas } from '../../../periodosmetas/services/periodosmetas.service';
import { OpcionesCargaDialogComponent } from '../opciones-carga-dialog/opciones-carga-dialog.component';

@Component({
  selector: 'app-metavendedor',
  templateUrl: './metavendedor.component.html',
  styleUrls: ['./metavendedor.component.scss']
})
export class MetavendedorComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('vendedorInput', {read: MatAutocompleteTrigger}) autocompleteTrigger!: MatAutocompleteTrigger;
  @ViewChild('vendedorSelect') vendedorSelect!: MatSelect;
  
  metaForm: FormGroup;
  searchControl = new FormControl('');
  vendedorControl = new FormControl('');
  periodoFilterControl = new FormControl('');
  isEditing = false;
  isLoading = false;
  editingId: number | null = null;
  selectedFile: File | null = null;
  currentFilters: { searchTerm?: string, periodo?: string } = {};
  
  vendedores: Vendedor[] = [];
  vendedoresFiltrados: Observable<Vendedor[]>;
  mostrarTodosVendedores = false;
  
  periodos: any[] = [];  // Aquí guardaremos todos los períodos
  periodosAbiertos: PeriodoMetas[] = [];
  
  displayedColumns: string[] = [
    'periodo', 
    'c_vend', 
    'valor_meta', 
    'meta_recalculada',
    'dias_ausentismos',
    'acciones'
  ];
  dataSource = new MatTableDataSource<MetaVendedor>();
  totalItems = 0;
  pageSize = 10;
  currentPage = 0;

  // Datos para la tabla de campos requeridos
  camposRequeridos = [
    {
      campo: 'periodo',
      formato: 'YYYYMM',
      ejemplo: '202401',
      descripcion: 'Año y mes de la meta'
    },
    {
      campo: 'c_vend',
      formato: 'Texto',
      ejemplo: 'VEND001',
      descripcion: 'Código del vendedor'
    },
    {
      campo: 'valor_meta',
      formato: 'Número',
      ejemplo: '1000000',
      descripcion: 'Valor de la meta en moneda'
    }
  ];

  columnasTabla = ['campo', 'formato', 'ejemplo', 'descripcion'];

  constructor(
    private formBuilder: FormBuilder,
    private localMetaVendedorService: LocalMetaVendedorService,
    private vendedorService: VendedorService,
    private notificationService: NotificationHelper,
    private dialog: MatDialog,
    private periodosmetasService: PeriodosmetasService,
    private cd: ChangeDetectorRef
  ) {
    this.initForm();

    // Sincronizar el vendedorControl con el campo c_vend del formulario
    this.vendedorControl.valueChanges.subscribe(vendedor => {
      if (vendedor && typeof vendedor === 'object') {
        this.metaForm.patchValue({
          c_vend: vendedor.codigo_vendedor
        }, { emitEvent: true });
      } else {
        this.metaForm.patchValue({
          c_vend: null
        }, { emitEvent: true });
      }
      // Forzar la validación del formulario
      this.metaForm.get('c_vend')?.updateValueAndValidity();
      this.cd.detectChanges();
    });
  }

  ngOnInit(): void {
    showSpinner('Cargando datos...');

    // Crear observables para cada llamada
    const metas$ = this.localMetaVendedorService.getPaginated(this.currentPage + 1, this.pageSize, 'JA', this.currentFilters);
    const vendedores$ = this.vendedorService.getVendedores('JA');
    const periodos$ = this.periodosmetasService.getAllPeriodos('JA');

    // Ejecutar todas las llamadas en paralelo y esperar que todas terminen
    forkJoin({
      metas: metas$,
      vendedores: vendedores$,
      periodos: periodos$
    }).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        hideSpinner();
      })
    ).subscribe({
      next: (response) => {
        // Procesar metas
        this.dataSource.data = response.metas.items;
        this.totalItems = response.metas.total;
        if (this.paginator) {
          this.paginator.length = this.totalItems;
          this.paginator.pageSize = this.pageSize;
          this.paginator.pageIndex = this.currentPage;
        }

        // Procesar vendedores
        this.vendedores = response.vendedores;

        // Configurar el filtrado de vendedores
        this.vendedoresFiltrados = this.vendedorControl.valueChanges.pipe(
          startWith(''),
          map(value => {
            if (this.mostrarTodosVendedores) {
              this.mostrarTodosVendedores = false;
              return this.vendedores;
            }
            const searchValue = typeof value === 'string' ? value : '';
            if (!searchValue) return this.vendedores.slice(0, 10);
            const filterValue = searchValue.toLowerCase();
            return this.vendedores.filter(vendedor => 
              vendedor.nombre_vendedor.toLowerCase().includes(filterValue) ||
              vendedor.codigo_vendedor.toLowerCase().includes(filterValue)
            );
          })
        );

        // Procesar periodos
        if (Array.isArray(response.periodos)) {
          this.periodos = response.periodos;
          this.periodosAbiertos = response.periodos.filter(p => p.estadoperiodo === 'ABIERTO');
        } else if (response.periodos?.data) {
          this.periodos = response.periodos.data;
          this.periodosAbiertos = response.periodos.data.filter(p => p.estadoperiodo === 'ABIERTO');
        }

        // Configurar el filtro de búsqueda
        this.searchControl.valueChanges.pipe(
          debounceTime(300),
          takeUntil(this.destroy$)
        ).subscribe(value => {
          if (value) {
            this.currentFilters = { ...this.currentFilters, searchTerm: value };
          } else {
            delete this.currentFilters.searchTerm;
          }
          this.loadData();
        });

        // Configurar el filtro de período
        this.periodoFilterControl.valueChanges.pipe(
          takeUntil(this.destroy$)
        ).subscribe(value => {
          if (value) {
            this.currentFilters = { ...this.currentFilters, periodo: value };
          } else {
            delete this.currentFilters.periodo;
          }
          this.loadData();
        });

        this.notificationService.successMessage('Datos cargados correctamente');
      },
      error: (error) => {
        this.notificationService.errorMessage('Error al cargar los datos: ' + error.message);
        hideSpinner();  // Asegurarnos de ocultar el spinner en caso de error
      }
    });
  }

  ngAfterViewInit() {
    this.paginator.page.subscribe((event: PageEvent) => {
      this.pageSize = event.pageSize;
      this.currentPage = event.pageIndex;
      this.loadData();
    });
  }

  async loadData(): Promise<void> {
    try {
      showSpinner('Cargando metas de vendedor...');
      
      const response = await this.localMetaVendedorService.getPaginated(
        this.currentPage + 1, 
        this.pageSize, 
        'JA',
        this.currentFilters
      ).toPromise();
      
      this.dataSource.data = response.items;
      this.totalItems = response.total;
      
      if (this.paginator) {
        this.paginator.length = this.totalItems;
        this.paginator.pageSize = this.pageSize;
        this.paginator.pageIndex = this.currentPage;
      }
      
      this.cd.detectChanges();
    } catch (error: any) {
      const errorMessage = error?.message || 'Error al cargar los datos';
      this.notificationService.errorMessage(errorMessage);
    } finally {
      hideSpinner();
    }
  }

  async loadVendedores(): Promise<void> {
    try {
      this.vendedores = await this.vendedorService.getVendedores('JA').toPromise();
    } catch (error: any) {
      const errorMessage = error?.message || 'Error al cargar los vendedores';
      this.notificationService.errorMessage(errorMessage);
    }
  }

  async cargarPeriodosAbiertos(): Promise<void> {
    try {
      const response = await this.periodosmetasService.getAllPeriodos('JA').toPromise();
      
      // Si la respuesta es un array directamente
      if (Array.isArray(response)) {
        this.periodos = response;  // Guardamos todos los períodos
        this.periodosAbiertos = response.filter(p => p.estadoperiodo === 'ABIERTO');
      }
      // Si la respuesta viene en formato {data: [...]}
      else if (response && response.data) {
        this.periodos = response.data;  // Guardamos todos los períodos
        this.periodosAbiertos = response.data.filter(p => p.estadoperiodo === 'ABIERTO');
      } 
      // Si no hay datos válidos
      else {
        this.periodosAbiertos = [];
      }

      // Verificar si se encontraron periodos abiertos
      if (this.periodosAbiertos.length === 0) {
      }
    } catch (error) {
      this.notificationService.errorMessage('Error al cargar los periodos');
      this.periodosAbiertos = [];
    }
  }

  displayVendedor = (vendedor: any): string => {
    if (!vendedor) return '';
    // Si es un string, es porque estamos escribiendo
    if (typeof vendedor === 'string') return vendedor;
    // Si es un objeto vendedor
    return `${vendedor.codigo_vendedor} - ${vendedor.nombre_vendedor}`;
  }

  getVendedorNombre(codigoVendedor: string): string {
    const vendedor = this.vendedores.find(v => v.codigo_vendedor === codigoVendedor);
    return vendedor ? `${vendedor.nombre_vendedor.toUpperCase()}` : codigoVendedor;
  }

  public mostrarTodosLosVendedores(): void {
    this.mostrarTodosVendedores = true;
    this.vendedorControl.setValue('');
    setTimeout(() => {
      if (this.autocompleteTrigger && !this.autocompleteTrigger.panelOpen) {
        this.autocompleteTrigger.openPanel();
      }
    });
  }
  
  onVendedorBlur(): void {
    // Dar tiempo para que se procese la selección antes de cerrar el panel
    setTimeout(() => {
      if (this.autocompleteTrigger && this.autocompleteTrigger.panelOpen) {
        this.autocompleteTrigger.closePanel();
      }
    }, 150);
  }

  onVendedorSelected(event: any): void {
    
    const vendedor = event.option.value;
    
    if (vendedor && typeof vendedor === 'object') {
      
      // IMPORTANTE: Evitar que se pierda el objeto vendedor
      // Primero actualizamos el formulario
      this.metaForm.patchValue({
        c_vend: vendedor.codigo_vendedor
      });
    
      
      // Forzar la validación del formulario
      this.metaForm.get('c_vend')?.updateValueAndValidity();
      
      // ENFOQUE DIRECTO: Cerrar el panel y mantener el valor
      // 1. Guardar una referencia al vendedor seleccionado
      const selectedVendedor = vendedor;
      
      // 2. Cerrar el panel de autocompletado
      if (this.autocompleteTrigger) {
        this.autocompleteTrigger.closePanel();
      }
      
      // 3. Forzar el foco a otro elemento para asegurar que el panel se cierre
      // Esto simula que el usuario hace clic fuera del campo
      document.activeElement && (document.activeElement as HTMLElement).blur();
      
      // 4. Establecer el valor del vendedor en el control SIN emitir evento
      // Hacerlo después de cerrar el panel para evitar conflictos
      this.vendedorControl.setValue(selectedVendedor, {emitEvent: false});
      
      // 5. Forzar la actualización de la vista
      this.cd.detectChanges();
    
    } else {
      console.error('El valor seleccionado no es un objeto vendedor válido:', vendedor);
    }
  }

  private _filtrarVendedores(value: string): Vendedor[] {
    if (this.mostrarTodosVendedores) {
      this.mostrarTodosVendedores = false;
      return this.vendedores;
    }
    
    if (!value) {
      return [];
    }

    const filterValue = value.toLowerCase();
    return this.vendedores.filter(vendedor => 
      vendedor.nombre_vendedor.toLowerCase().includes(filterValue) ||
      vendedor.codigo_vendedor.toLowerCase().includes(filterValue)
    );
  }

  async onSubmit(): Promise<void> {
    if (this.metaForm.valid) {
      try {
        showSpinner('Guardando meta...');
        
        const formValues = this.metaForm.getRawValue();
        const metaData: MetaVendedor = {
          periodo: formValues.periodo,
          c_vend: formValues.c_vend.toUpperCase(),
          valor_meta: formValues.valor_meta,
          meta_recalculada: 0,
          dias_ausentismos: 0
        };

        if (this.isEditing && this.editingId) {
          await this.localMetaVendedorService.update(this.editingId, metaData, 'JA').toPromise();
          this.notificationService.successMessage('Meta actualizada correctamente');
          this.isEditing = false;
          this.editingId = null;
        } else {
          await this.localMetaVendedorService.create(metaData, 'JA').toPromise();
          this.notificationService.successMessage('Meta creada correctamente');
        }
        
        this.resetForm();
        await this.loadData();
        this.cd.detectChanges(); // Forzar actualización de la vista

      } catch (error: any) {
        this.notificationService.errorMessage(error.message || 'Error al guardar la meta');
      } finally {
        hideSpinner();
        this.cd.detectChanges(); // Asegurar que la UI se actualice al finalizar
      }
    }
  }

  isPeriodoAbierto(periodo: string): boolean {
    return this.periodosAbiertos.some(p => p.periodo === periodo);
  }

  editarMeta(meta: MetaVendedor): void {
    if (!this.isPeriodoAbierto(meta.periodo)) {
      this.notificationService.warningMessage('No se puede editar un registro de un periodo cerrado');
      return;
    }
    
    this.isEditing = true;
    this.editingId = meta.id ?? null;
    
    // Buscar el vendedor correspondiente
    const vendedor = this.vendedores.find(v => v.codigo_vendedor === meta.c_vend);
    
    this.metaForm.patchValue({
      periodo: meta.periodo,
      c_vend: meta.c_vend,
      valor_meta: meta.valor_meta
    });

    if (vendedor) {
      this.vendedorControl.setValue(vendedor);
    }

    this.vendedorControl.disable();
    this.metaForm.get('periodo')?.disable();
  }

  eliminarMeta(meta: MetaVendedor): void {
    if (!this.isPeriodoAbierto(meta.periodo)) {
      this.notificationService.warningMessage('No se puede eliminar un registro de un periodo cerrado');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        message: '¿Está seguro que desea eliminar esta meta?'
      }
    });

    dialogRef.afterClosed().pipe(
      takeUntil(this.destroy$)
    ).subscribe(result => {
      if (result) {
        this.isLoading = true;
        showSpinner('Eliminando meta...');
        
        this.localMetaVendedorService.delete(meta.id!, 'JA').pipe(
          finalize(() => {
            this.isLoading = false;
            hideSpinner();
          })
        ).subscribe({
          next: () => {
            this.notificationService.successMessage('Meta eliminada correctamente');
            this.loadData();
          },
          error: () => {
            this.notificationService.errorMessage('Error al eliminar la meta');
          }
        });
      }
    });
  }

  verDetalles(meta: MetaVendedor): void {
    this.dialog.open(DetalleMetaDialogComponent, {
      width: '700px',
      data: {
        meta: meta,
        vendedorNombre: this.getVendedorNombre(meta.c_vend)
      }
    });
  }

  resetForm(): void {
    this.metaForm.reset();
    this.isEditing = false;
    this.editingId = null;
    this.vendedorControl.reset();
    
    // Habilitar el control de periodo
    this.metaForm.get('periodo')?.enable();
    
    this.cd.detectChanges(); // Forzar actualización del formulario
  }

  async procesarArchivo(): Promise<void> {
    if (!this.selectedFile) {
      return;
    }

    // Leer el archivo
    let datos: MetaVendedor[] = [];
    try {
      if (this.selectedFile.name.endsWith('.xlsx') || this.selectedFile.name.endsWith('.xls')) {
        const workbook = await this.leerArchivoExcel(this.selectedFile);
        datos = this.procesarDatosExcel(workbook);
      } else if (this.selectedFile.name.endsWith('.csv')) {
        datos = await this.leerArchivoCSV(this.selectedFile);
      } else {
        throw new Error('Formato de archivo no soportado');
      }
    } catch (error: any) {
      this.notificationService.errorMessage(error.message || 'Error al procesar el archivo');
      return;
    }

    if (datos.length === 0) {
      this.notificationService.warningMessage('No se encontraron datos válidos en el archivo');
      return;
    }

    // Obtener el periodo del primer registro
    const periodo = datos[0].periodo;

    // Validar formato del periodo
    if (!periodo.match(/^\d{6}$/)) {
      this.notificationService.errorMessage('El período debe tener formato YYYYMM');
      return;
    }

    // Validar que el periodo exista y esté abierto
    const periodoExistente = this.periodosAbiertos.find(p => p.periodo === periodo);
    if (!periodoExistente) {
      this.notificationService.errorMessage(`El periodo ${periodo} no existe o está cerrado`);
      return;
    }

    // Mostrar diálogo de confirmación solo si el periodo es válido
    const dialogRef = this.dialog.open(OpcionesCargaDialogComponent, {
      width: '600px',
      data: {
        periodo: periodo,
        cantidadRegistros: datos.length
      }
    });

    const result = await dialogRef.afterClosed().toPromise();

    if (!result || !result.confirmado) {
      return;
    }

    try {
      showSpinner('Procesando archivo...');
      
      // Procesar según la opción seleccionada
      let response: BulkLoadResponse;
      try {
        response = await (result.tipo === 'REEMPLAZAR' ?
          this.localMetaVendedorService.bulkLoad('JA', datos, true, periodo) :
          this.localMetaVendedorService.bulkLoad('JA', datos, false)
        ).toPromise();

        // Si hay registros fallidos o el mensaje indica un error
        if (response.registrosFallidos?.length > 0 || response.registrosInsertados === 0) {
          this.notificationService.errorMessage(response.mensaje || 'Se encontraron errores en la carga');
        } else {
          this.notificationService.successMessage(response.mensaje || 'Proceso completado exitosamente');
        }

        // Limpiar selección y recargar datos
        this.selectedFile = null;
        await this.loadData();

      } catch (error: any) {
        this.notificationService.errorMessage(
          error.error?.detail || error.message || 'Error al procesar el archivo'
        );
      }

    } catch (error: any) {
      this.notificationService.errorMessage(
        error.error?.detail || error.message || 'Error en el proceso'
      );
    } finally {
      hideSpinner();
    }
  }

  private procesarDatosExcel(workbook: XLSX.WorkBook): MetaVendedor[] {
    
    const datos: MetaVendedor[] = [];
    const hojaExcel = workbook.Sheets[workbook.SheetNames[0]];
    const datosExcel = XLSX.utils.sheet_to_json<ExcelRow>(hojaExcel);
    
    // Validar que solo existan las columnas requeridas
    if (datosExcel.length > 0) {
      const primeraFila = datosExcel[0];
      const columnas = Object.keys(primeraFila);
      
      // Verificar si hay columnas adicionales
      const columnasPermitidas = ['periodo', 'c_vend', 'valor_meta'];
      const columnasNoPermitidas = columnas.filter(col => !columnasPermitidas.includes(col));
      
      if (columnasNoPermitidas.length > 0) {
        const mensaje = `El archivo contiene columnas no permitidas: ${columnasNoPermitidas.join(', ')}. Solo se permiten: periodo, c_vend, valor_meta`;
        this.notificationService.errorMessage(mensaje);
        throw new Error(mensaje);
      }
    }
    
    // Conjunto para almacenar periodos únicos
    const periodosUnicos = new Set<string>();
    
    // Mapa para detectar duplicados (key: c_vend)
    const vendedoresProcesados = new Map<string, number>();
    let tieneVendedoresDuplicados = false;
    let tieneValoresNegativos = false;
    let tieneValoresCero = false;

    datosExcel.forEach((fila, index) => {
      const periodo = String(fila.periodo).trim();
      const vendedor = String(fila.c_vend).trim().toUpperCase();
      const valorMeta = Number(fila.valor_meta);
      
      // Agregar periodo al conjunto de periodos únicos
      periodosUnicos.add(periodo);
      
      // Validar valor negativo o cero
      if (valorMeta < 0) {
        tieneValoresNegativos = true;
      } else if (valorMeta === 0) {
        tieneValoresCero = true;
      }
      
      // Verificar si ya existe este vendedor
      if (vendedoresProcesados.has(vendedor)) {
        tieneVendedoresDuplicados = true;
      } else {
        // Si no está duplicado y no es negativo ni cero, agregarlo al mapa y a los datos
        vendedoresProcesados.set(vendedor, index);
        if (valorMeta > 0) {
          datos.push({
            periodo: periodo,
            c_vend: vendedor,
            valor_meta: valorMeta
          });
        }
      }
    });

    // Construir mensaje de error si hay problemas
    const errores: string[] = [];
    
    // Validar que solo haya un periodo
    if (periodosUnicos.size > 1) {
      const mensaje = 'Solo se permite el cargue de un periodo por vez';
      // No generar CSV en caso de error de múltiples periodos
      this.notificationService.errorMessage(mensaje);
      throw new Error(mensaje);
    }
    
    if (tieneVendedoresDuplicados) {
      errores.push('- Vendedores duplicados');
    }

    if (tieneValoresNegativos) {
      errores.push('- Valores negativos');
    }

    // Detener el proceso si hay valores cero
    if (tieneValoresCero) {
      const mensaje = 'No se permite valore cero en la columna valor_meta';
      this.notificationService.errorMessage(mensaje);
      throw new Error(mensaje);
    }

    if (errores.length > 0) {
      const mensaje = 'Se encontraron los siguientes problemas:\n' + errores.join('\n');
      this.notificationService.warningMessage(mensaje);
    }
    return datos;
  }

  private async leerArchivoExcel(file: File): Promise<XLSX.WorkBook> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          resolve(workbook);
        } catch (error: any) {
          reject(error);
        }
      };
      reader.onerror = (error: any) => reject(error);
      reader.readAsArrayBuffer(file);
    });
  }

  private async leerArchivoCSV(file: File): Promise<MetaVendedor[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const datos: MetaVendedor[] = [];
          const contenido = e.target.result;
          const filas = contenido.split('\n');
          filas.forEach((fila, index) => {
            if (index > 0) {
              const campos = fila.split(',');
              if (campos.length === 3) {
                const vendedor = campos[1].trim().toUpperCase();
                const valorMeta = Number(campos[2]);
                if (valorMeta >= 0) {
                  datos.push({
                    periodo: campos[0].trim(),
                    c_vend: vendedor,
                    valor_meta: valorMeta
                  });
                }
              }
            }
          });
          resolve(datos);
        } catch (error: any) {
          reject(error);
        }
      };
      reader.onerror = (error: any) => reject(error);
      reader.readAsText(file);
    });
  }

  private generarCSV(resultados: ResultadoCarga[]): void {
    
    if (!resultados || resultados.length === 0) {
      return;
    }

    try {
      const csvData = resultados.map(r => ({
        periodo: r.periodo,
        c_vend: r.c_vend,
        valor_meta: r.valor_meta || 0,
        estado: r.estado || 'ERROR',
        error: r.error || ''
      }));


      const worksheet = XLSX.utils.json_to_sheet(csvData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Errores');

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `errores_carga_${timestamp}.xlsx`;
      
      XLSX.writeFile(workbook, fileName);
      this.notificationService.successMessage('Se ha generado el archivo de errores');
    } catch (error) {
      this.notificationService.errorMessage('Error al generar el archivo de errores');
    }
  }

  cancelarSeleccionArchivo() {
    this.selectedFile = null;
    // Limpiar el input file para permitir seleccionar el mismo archivo nuevamente
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  mostrarListaVendedores(): void {
    this.mostrarTodosVendedores = true;
    this.vendedorControl.setValue('');
    if (this.autocompleteTrigger) {
      this.autocompleteTrigger.openPanel();
    }
  }

  onValorMetaInput(event: any): void {
    let value = event.target.value;
    // Remover todo excepto números
    value = value.replace(/[^0-9]/g, '');
    this.metaForm.get('valor_meta')?.setValue(Number(value));
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      if (fileExt === 'xlsx' || fileExt === 'xls' || fileExt === 'csv') {
        this.selectedFile = file;
      } else {
        this.notificationService.errorMessage('Por favor seleccione un archivo Excel válido (.xlsx, .xls o .csv)');
        this.selectedFile = null;
      }
    }
  }

  onCancel(): void {
    this.resetForm();
  }

  private setupFormObservables(): void {
    // Configurar el observable de vendedores para el formulario
    this.vendedoresFiltrados = this.vendedorControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        if (this.mostrarTodosVendedores) {
          this.mostrarTodosVendedores = false;
          return this.vendedores;
        }

        // Si es un string (búsqueda), filtrar los vendedores

        const searchTerm = typeof value === 'string' ? value : '';
        return this.vendedores.filter(vendedor =>
          vendedor.codigo_vendedor.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vendedor.nombre_vendedor.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }),
      takeUntil(this.destroy$)
    );

    // Suscribirse a los cambios del vendedor seleccionado
    this.vendedorControl.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(value => {
      if (value && typeof value === 'object') {
        this.metaForm.patchValue({
          c_vend: value.codigo_vendedor
        }, { emitEvent: false });
      }
    });
  }

  initForm(): void {
    this.metaForm = this.formBuilder.group({
      periodo: ['', [
        Validators.required, 
        Validators.pattern(/^\d{4}(0[1-9]|1[0-2])$/),
        Validators.minLength(6),
        Validators.maxLength(6)
      ]],
      c_vend: ['', Validators.required],
      valor_meta: ['', [Validators.required, Validators.min(0)]]
    });

    // Inicializar el observable de vendedores después de crear el formulario
    this.setupFormObservables();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
